/**
 * דו"ח בקרת מדיניות — the client-facing policy-control table.
 *
 * One compact line per rule × fund (plus a value-weighted סיכום line per rule):
 * the actual figure, a range bar — the allowed band drawn on a track with the
 * actual as a marker, so "inside or outside, and by how much" is read at a
 * glance — and the headroom (הפרש), green inside the limit and red outside.
 *
 * In the single-fund view the rules become the rows, since there is only one
 * fund to show.
 */
import { Card, EmptyState } from './Card.js';
import { FundLogo } from './FundChip.js';
import { cn, fmtPct, ltrNum } from '../utils.js';
import type { PolicyReportView, PolicyResultRow } from '@agio/core';

export const policyHasData = (view: PolicyReportView | null | undefined): view is PolicyReportView =>
  !!view && view.results.length > 0;

/** "עד 25%" / "לפחות 50%" / "5%–10%" / "עד 4" — how the masters print a limit. */
export function fmtLimit(r: Pick<PolicyResultRow, 'unit' | 'limit_min' | 'limit_max'>): string {
  const f = (v: number) => (r.unit === 'years' ? ltrNum(String(+v.toFixed(2))) : fmtPct(v, v * 100 % 1 === 0 ? 0 : 2));
  if (r.limit_min !== null && r.limit_max !== null) return ltrNum(`${f(r.limit_min)}–${f(r.limit_max)}`);
  if (r.limit_max !== null) return `עד ${f(r.limit_max)}`;
  if (r.limit_min !== null) return `לפחות ${f(r.limit_min)}`;
  return '—';
}

export function fmtValue(unit: PolicyResultRow['unit'], v: number | null, digits = 2): string {
  if (v === null) return '—';
  return unit === 'years' ? ltrNum(v.toFixed(digits)) : fmtPct(v, digits);
}

const tone = (r: PolicyResultRow) =>
  r.status === 'breach' ? 'text-destructive' : r.status === 'ok' ? 'text-success' : 'text-muted-foreground';

/**
 * The allowed band on a track from zero, with the actual as a marker. The
 * track is scaled per rule (a 4% ceiling and a 60% ceiling must not share a
 * scale) and shared by every line of that rule, so funds are comparable.
 */
function RangeBar({ r, scale }: { r: PolicyResultRow; scale: number }) {
  const pct = (v: number) => `${Math.min(100, Math.max(0, (v / scale) * 100))}%`;
  const lo = r.limit_min ?? 0;
  const hi = r.limit_max ?? scale;
  const v = r.effective;
  const marker = r.status === 'breach' ? 'bg-destructive' : r.status === 'ok' ? 'bg-success' : 'bg-muted-foreground';
  return (
    <div
      dir="ltr"
      className="relative h-3.5 w-full min-w-24"
      title={`${fmtLimit(r)} · בפועל ${fmtValue(r.unit, v)}${r.diff !== null ? ` · הפרש ${fmtValue(r.unit, r.diff, 1)}` : ''}`}
    >
      <div className="absolute inset-x-0 top-1/2 h-1.5 -translate-y-1/2 rounded-full bg-muted-foreground/20" />
      <div
        className="absolute top-1/2 h-1.5 -translate-y-1/2 rounded-full bg-success/40"
        style={{ left: pct(lo), width: `calc(${pct(hi)} - ${pct(lo)})` }}
      />
      {v !== null && (
        <div className={cn('absolute top-0 h-3.5 w-0.5 rounded-sm ring-2 ring-card', marker)} style={{ left: `calc(${pct(v)} - 1px)` }} />
      )}
    </div>
  );
}

/** One scale per rule: the largest of its limits and every actual, with room to spare. */
function scaleFor(rows: PolicyResultRow[]): number {
  const top = Math.max(0, ...rows.flatMap((r) => [r.limit_max ?? 0, r.limit_min ?? 0, r.effective ?? 0]));
  const scale = top * 1.3;
  return rows[0]?.unit === 'pct' ? Math.min(1, scale || 0.1) : scale || 1;
}

interface RuleGroup { rule_id: string; label_he: string; rows: PolicyResultRow[] }

function groupByRule(results: PolicyResultRow[]): RuleGroup[] {
  const groups = new Map<string, RuleGroup>();
  for (const r of results) {
    if (!groups.has(r.rule_id)) groups.set(r.rule_id, { rule_id: r.rule_id, label_he: r.label_he, rows: [] });
    groups.get(r.rule_id)!.rows.push(r);
  }
  return [...groups.values()];
}

const cell = 'px-2 py-1.5 whitespace-nowrap';

function DiffCell({ r }: { r: PolicyResultRow }) {
  return (
    <td className={cn(cell, 'text-end tabular-nums font-semibold', tone(r))} title={r.status === 'no_data' ? (r.inputs.missing ?? undefined) : undefined}>
      {r.status === 'no_data' ? 'אין נתון' : fmtValue(r.unit, r.diff, 1)}
    </td>
  );
}

export function PolicyControlTable({ view, fundId }: { view: PolicyReportView | null; fundId?: string | null }) {
  const title = `דו"ח בקרת מדיניות${view ? ` · ${view.report.period.slice(0, 7).split('-').reverse().join('/')}` : ''}`;
  if (!policyHasData(view)) return <Card title="דו&quot;ח בקרת מדיניות"><EmptyState label="טרם פורסמה בקרת מדיניות לחודש זה" /></Card>;

  const groups = groupByRule(view.results);
  const head = 'px-2 py-1.5 text-xs font-normal text-muted-foreground';

  if (fundId) {
    const rows = view.results.filter((r) => r.fund_id === fundId);
    if (rows.length === 0) return <Card title={title}><EmptyState label="התיק אינו נכלל בבקרת המדיניות לחודש זה" /></Card>;
    return (
      <Card title={title}>
        <div className="overflow-x-auto">
          <table className="print-dense-table w-full text-sm">
            <thead>
              <tr className="border-b border-border text-right">
                <th className={head}>כלל</th>
                <th className={cn(head, 'text-end')}>מגבלת מדיניות</th>
                <th className={cn(head, 'text-end')}>מצב בפועל</th>
                <th className={cn(head, 'w-40 md:w-56')} />
                <th className={cn(head, 'text-end')}>הפרש</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.result_id} className="border-b border-border/50">
                  <td className={cn(cell, 'font-medium')}>{r.label_he}</td>
                  <td className={cn(cell, 'text-end tabular-nums text-muted-foreground')}>{fmtLimit(r)}</td>
                  <td className={cn(cell, 'text-end tabular-nums')}>{fmtValue(r.unit, r.effective)}</td>
                  <td className={cell}><RangeBar r={r} scale={scaleFor([r])} /></td>
                  <DiffCell r={r} />
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    );
  }

  return (
    <Card title={title}>
      <div className="overflow-x-auto">
        <table className="print-dense-table w-full text-sm">
          <thead>
            <tr className="border-b border-border text-right">
              <th className={head}>מנהל</th>
              <th className={cn(head, 'text-end')}>מצב בפועל</th>
              <th className={cn(head, 'w-40 md:w-56')} />
              <th className={cn(head, 'text-end')}>הפרש</th>
            </tr>
          </thead>
          <tbody>
            {groups.map((g) => {
              const scale = scaleFor(g.rows);
              const funds = g.rows.filter((r) => r.fund_id !== null);
              const summary = g.rows.find((r) => r.fund_id === null);
              return (
                <RuleRows key={g.rule_id} g={g} funds={funds} summary={summary} scale={scale} />
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function RuleRows({ g, funds, summary, scale }: { g: RuleGroup; funds: PolicyResultRow[]; summary: PolicyResultRow | undefined; scale: number }) {
  const limit = g.rows[0] ? fmtLimit(g.rows[0]) : '';
  return (
    <>
      <tr className="border-b border-border/50 bg-muted/40">
        <td colSpan={4} className="px-2 pt-2.5 pb-1 text-xs font-semibold">
          {g.label_he} <span className="ms-1 font-normal text-muted-foreground tabular-nums">{limit}</span>
        </td>
      </tr>
      {funds.map((r) => (
        <tr key={r.result_id} className="border-b border-border/40">
          <td className={cn(cell, 'flex items-center gap-1.5')}>
            <FundLogo name={r.fund ?? ''} size={14} />
            {r.fund}
          </td>
          <td className={cn(cell, 'text-end tabular-nums')}>{fmtValue(r.unit, r.effective)}</td>
          <td className={cell}><RangeBar r={r} scale={scale} /></td>
          <DiffCell r={r} />
        </tr>
      ))}
      {summary && (
        <tr className="border-b border-border/60 font-semibold">
          <td className={cell}>סיכום</td>
          <td className={cn(cell, 'text-end tabular-nums')}>{fmtValue(summary.unit, summary.effective)}</td>
          <td className={cell}><RangeBar r={summary} scale={scale} /></td>
          <DiffCell r={summary} />
        </tr>
      )}
    </>
  );
}
